import { useEffect, useState } from 'react'
import { Button, Input, Space } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { useToast } from '@/contexts/ToastContext'
import { useUpdateAdvisoryCallMutation } from '@/features/advisory-ai/hooks'

const { TextArea } = Input

type ReportSummarySectionProps = {
  callId: string
  clientId: string
  summary: string | null | undefined
}

export function ReportSummarySection({ callId, clientId, summary }: ReportSummarySectionProps) {
  const { addToast } = useToast()
  const updateCallMutation = useUpdateAdvisoryCallMutation(callId, clientId)
  const [isEditingSummary, setIsEditingSummary] = useState(false)
  const [meetingSummary, setMeetingSummary] = useState(summary || '')
  const [editingSummaryText, setEditingSummaryText] = useState(summary || '')

  useEffect(() => {
    const nextSummary = summary || ''
    setMeetingSummary(nextSummary)
    setEditingSummaryText(nextSummary)
  }, [summary])

  const handleSaveSummary = async () => {
    try {
      await updateCallMutation.mutateAsync({ summary: editingSummaryText })
      setMeetingSummary(editingSummaryText)
      setIsEditingSummary(false)
      addToast('Meeting summary updated successfully', 'success')
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to update meeting summary', 'error')
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Meeting Summary</h3>
        {!isEditingSummary ? (
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingSummaryText(meetingSummary)
              setIsEditingSummary(true)
            }}
          />
        ) : (
          <Space>
            <Button
              onClick={() => {
                setEditingSummaryText(meetingSummary)
                setIsEditingSummary(false)
              }}
            >
              Cancel
            </Button>
            <Button type="primary" loading={updateCallMutation.isPending} onClick={() => void handleSaveSummary()}>
              Save
            </Button>
          </Space>
        )}
      </div>
      {isEditingSummary ? (
        <TextArea
          value={editingSummaryText}
          onChange={(e) => setEditingSummaryText(e.target.value)}
          rows={6}
          className="w-full"
        />
      ) : (
        <p className="text-gray-700 whitespace-pre-wrap">{meetingSummary}</p>
      )}
    </div>
  )
}
